import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { Logo } from "@/components/brand/logo";
import { authClient } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

const navItems = [
	{
		label: "Dashboard",
		path: "/dashboard",
		icon: "dashboard",
		isActive: (pathname: string) => pathname === "/dashboard",
	},
	{
		label: "Chat",
		path: "/dashboard/chat",
		icon: "forum",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/chat"),
	},
	{
		label: "API Keys",
		path: "/dashboard/api-keys",
		icon: "vpn_key",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/api-keys"),
	},
	{
		label: "Job History",
		path: "/dashboard/generations",
		icon: "history",
		isActive: (pathname: string) =>
			pathname.startsWith("/dashboard/generations"),
	},
	{
		label: "Models",
		path: "/dashboard/models",
		icon: "auto_awesome",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/models"),
	},
	{
		label: "Usage",
		path: "/dashboard/usage",
		icon: "monitoring",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/usage"),
	},
	{
		label: "Billing",
		path: "/dashboard/billing",
		icon: "account_balance_wallet",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/billing"),
	},
	{
		label: "Settings",
		path: "/dashboard/settings",
		icon: "settings",
		isActive: (pathname: string) => pathname.startsWith("/dashboard/settings"),
	},
] as const;

export function DashboardSidebar() {
	const location = useLocation();
	const navigate = useNavigate();

	async function handleLogout() {
		await authClient.signOut();
		navigate({ to: "/" });
	}

	return (
		<aside
			aria-label="Dashboard sidebar"
			className="fixed left-0 top-0 h-screen w-64 z-40 hidden md:flex flex-col py-8"
			style={{
				backgroundColor: "var(--km-surface-low)",
				boxShadow: "rgba(255,255,255,0.06) 0px -1px 0px 0px inset, rgba(0,0,0,0.3) 1px 0px 0px 0px",
				borderTop: "2px solid #6B3FF2",
			}}
		>
			{/* Logo */}
			<div className="px-6 mb-1">
				<Link to="/dashboard" aria-label="AgentFramer home">
					<Logo size="sm" disableLink />
				</Link>
				<p
					className="mt-1 text-[10px] tracking-wider uppercase font-medium"
					style={{ color: "var(--km-on-surface-muted)" }}
				>
					v1.0
				</p>
			</div>

			{/* Nav items */}
			<nav aria-label="Dashboard navigation" className="flex-1 mt-6 px-3 space-y-0.5">
				{navItems.map((item) => {
					const active = item.isActive(location.pathname);
					return (
						<Link
							key={item.path}
							to={item.path}
							aria-current={active ? "page" : undefined}
							className={cn(
								"flex items-center gap-3 px-3 py-2.5 text-sm rounded-[6px] transition-all duration-150 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60",
								active
									? "text-[#6B3FF2] bg-[rgba(234,40,4,0.07)]"
									: "text-[var(--km-on-surface-muted)] hover:text-[var(--km-on-surface)] hover:bg-[var(--km-surface-high)]",
							)}
						>
							<span
								className="material-symbols-outlined shrink-0"
								aria-hidden="true"
								style={{
									fontSize: "18px",
									fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0",
								}}
							>
								{item.icon}
							</span>
							<span className="text-[13px] font-medium">
								{item.label}
							</span>
						</Link>
					);
				})}
			</nav>

			{/* Bottom section */}
			<div
				className="px-3 space-y-1 pt-4"
				style={{ boxShadow: "rgba(255,255,255,0.06) 1px 0px 0px 0px inset" }}
			>
				<Link
					to="/dashboard/billing"
					search={{ checkout: undefined }}
					className="flex items-center justify-center gap-2 w-full px-4 py-2.5 font-semibold text-xs rounded-[6px] transition-all mb-3 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{
						background: "rgba(234,40,4,0.08)",
						boxShadow: "rgba(234,40,4,0.3) 0px 0px 0px 1px",
						color: "#6B3FF2",
					}}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLElement).style.background = "rgba(234,40,4,0.14)";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLElement).style.background = "rgba(234,40,4,0.08)";
					}}
					onFocus={(e) => {
						(e.currentTarget as HTMLElement).style.background = "rgba(234,40,4,0.14)";
					}}
					onBlur={(e) => {
						(e.currentTarget as HTMLElement).style.background = "rgba(234,40,4,0.08)";
					}}
				>
					<span
						className="material-symbols-outlined shrink-0"
						aria-hidden="true"
						style={{ fontSize: "16px", fontVariationSettings: "'FILL' 1" }}
					>
						account_balance_wallet
					</span>
					Top Up Balance
				</Link>

				<Link
					to="/dashboard/help"
					className="flex items-center gap-3 px-3 py-2 rounded-[6px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)" }}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
					}}
					onFocus={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
					}}
					onBlur={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
					}}
				>
					<span
						className="material-symbols-outlined shrink-0"
						aria-hidden="true"
						style={{ fontSize: "16px" }}
					>
						help
					</span>
					<span className="text-[13px] font-medium">Help</span>
				</Link>

				<button
					type="button"
					onClick={handleLogout}
					className="flex items-center gap-3 w-full px-3 py-2 rounded-[6px] transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)" }}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
					}}
					onFocus={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
					}}
					onBlur={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
					}}
				>
					<span
						className="material-symbols-outlined shrink-0"
						aria-hidden="true"
						style={{ fontSize: "16px" }}
					>
						logout
					</span>
					<span className="text-[13px] font-medium">Logout</span>
				</button>
			</div>
		</aside>
	);
}
